import { mods, RequiredMods, theme } from '@approvalmax/theme';
import { StyledMods } from '@styled-kit/mods';
import styled, { css } from 'styled-components';

import { Container } from '../../components';
import { Grid } from '../Grid/Grid';
import { SimpleLayoutProps } from './SimpleLayout.types';

const areas = (breakpoint: RequiredMods['containerBreakpoint']) => css`
    /* these first and last lines w/ "..." allow us to shrink the middle line content
     together whether we want to place it on top or in the middle of the page */
    grid-template-areas:
        '... ... ...'
        'leftSide logo rightSide'
        'leftSide top rightSide'
        'leftSide content rightSide'
        'leftSide footer rightSide'
        'leftSide ... rightSide';

    @media (max-width: ${theme.container.breakpoint[breakpoint]}) {
        grid-template-rows: none;
        grid-template-areas:
            'logo logo logo'
            'top top top'
            'content content content'
            'footer footer footer'
            'leftSide leftSide leftSide'
            'rightSide rightSide rightSide';
    }
`;

export const containerWidth = css`
    ${mods.size.large`
        width: 960px;

        @media (max-width: ${theme.container.breakpoint.medium}) {
            width: 100%;
        }
    `};

    ${mods.size.medium`
        width: 800px;

        @media (max-width: ${theme.container.breakpoint.small}) {
            width: 100%;
        }
    `};

    ${mods.size.small`
        width: 560px;

        @media (max-width: ${theme.container.breakpoint.xsmall}) {
            width: 100%;
        }
    `};

    ${mods.size.xsmall`
        width: 480px;

        @media (max-width: ${theme.container.breakpoint.xsmall}) {
            width: 100%;
        }
    `};
`;

export const Wrapper = styled(Grid)<StyledMods<Pick<SimpleLayoutProps, 'size' | 'verticalAlign'>>>`
    height: 100%;

    ${mods.not.size`
        grid-template-columns: auto 1fr auto;
        grid-template-rows: auto max-content auto 1fr auto;
        ${areas('medium')};

        @media (max-width: ${theme.container.breakpoint.medium}) {
            /* this is an override of the rule within "areas" mixin */
            grid-template-rows: max-content auto 1fr auto;
        }
    `};

    ${mods.size`
        align-content: ${({ $verticalAlign }) => $verticalAlign};
        justify-content: center;

        ${mods('verticalAlign')((value) => {
            if (value === 'start') {
                return 'grid-template-rows: repeat(5, auto) 1fr';
            }

            return 'grid-template-rows: 1fr repeat(4, auto) 1fr';
        })};

        @media (max-width: ${theme.container.breakpoint.xsmall}) {
            align-content: start;
        }
    `};

    ${mods.size.large`
        ${areas('large')};

        @media (max-width: ${theme.container.breakpoint.medium}) {
            justify-content: stretch;
        }

    `};

    ${mods.size.medium`
        ${areas('large')};

        @media (max-width: ${theme.container.breakpoint.medium}) {
            justify-content: stretch;
        }

    `};

    ${mods.size.small`
        ${areas('medium')};

        @media (max-width: ${theme.container.breakpoint.xsmall}) {
            justify-content: stretch;
        }
    `};

    ${mods.size.xsmall`
        ${areas('medium')};

        @media (max-width: ${theme.container.breakpoint.xsmall}) {
            justify-content: stretch;
        }
    `};
`;

export const ContainerStyled = styled(Container)`
    @media (min-width: ${theme.container.viewport.large}) {
        width: ${theme.container.content.medium};
    }
`;

export const Root = styled.div<StyledMods<Pick<SimpleLayoutProps, 'size'>>>`
    background-color: ${theme.color.midnight20};
    /* grid plays crucial role by stretching child content to 100% of own height
    even though child might have smaller height than Root */
    display: grid;
    min-height: 100vh;
    padding: 24px 0;

    @media (max-width: ${theme.container.breakpoint.xsmall}) {
        background-color: ${theme.color.white100};
        background-image: none;
        height: initial;
    }
`;
